/**
 * 单个事件筛选器项组件
 */
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import classNames from 'classnames';
import { useIntl } from 'umi';
import { v4 as uuidv4 } from 'uuid';
import { Popover, Button, Icon } from 'tea-component';
import Bubble from '@/pages/home/components/TeaComponents/Bubble';
import EventPanel from '../EventPanel';
import { getLanguage } from '@home/utils/common';
import EditInput from '../../../EditInput';
import { IEventFilterItemProps } from './types';
import { IEventFilterItem } from '../../types';
import { LANGUAGE_EN } from '@home/_config/config';
import './index.scss';

const DEFAULT_EVENT_FILTER_VALUE: IEventFilterItem = {
  id: uuidv4(),
  displayNameEn: '',
  displayNameCn: '',
  eventName: '',
  eventParameter: {
    type: 'market',
    regions: [],
    markets: [],
  },
  hoursSinceRegister: '',
  hoursSinceRegisterCustom: '',
  minValue: '',
  maxValue: '',
  aggregationType: '',
  occurCountOperator: '',
  occurCountValue: [],
};

const EventFilterItem: React.FC<IEventFilterItemProps> = (props) => {
  const {
    eventList,
    hoursSinceRegisterOptions,
    value,
    optionCustomMetrics,
    defaultExpand = false,
    displayConfig,
    visible: controlledVisible,
    onOpen,
    onClose,
    onChange,
    onConfirm,
    onDelete,
    onRename,
  } = props;
  const { locale, formatMessage } = useIntl();
  const isEN = locale === LANGUAGE_EN;
  const [uncontrolledVisible, setUncontrolledVisible] = useState(defaultExpand);
  const [tempEventFilterValue, setTempEventFilterValue] = useState<IEventFilterItem>(
    value || DEFAULT_EVENT_FILTER_VALUE
  );

  const isControlled = controlledVisible !== undefined;
  const eventFilterVisible = isControlled ? controlledVisible : uncontrolledVisible;

  useEffect(() => {
    if (value) {
      setTempEventFilterValue(value);
    }
  }, [value]);

  // 一级浮窗仅通过确认/取消关闭；点击空白时不关闭一级浮窗（仅关闭二级浮窗）
  const handleVisibleChange = useCallback((visible: boolean) => {
    if (visible) {
      onOpen?.();
      if (!isControlled) {
        setUncontrolledVisible(true);
      }
    }
    // visible === false 时（如点击空白）不关闭一级浮窗，不重置 temp 值
  }, [isControlled, onOpen]);

  const handleEventFilterConfirm = useCallback((filterValue?: IEventFilterItem) => {
    const finalValue = filterValue || tempEventFilterValue;
    const abExperimentValue: IEventFilterItem = {
      ...finalValue,
    };
    onChange?.(abExperimentValue);
    onConfirm?.(abExperimentValue);
    onClose?.();
    if (!isControlled) {
      setUncontrolledVisible(false);
    }
  }, [tempEventFilterValue, isControlled, onChange, onConfirm, onClose]);

  const handleEventFilterCancel = useCallback(() => {
    if (value) {
      setTempEventFilterValue(value);
    } else {
      setTempEventFilterValue(DEFAULT_EVENT_FILTER_VALUE);
    }
    onClose?.();
    if (!isControlled) {
      setUncontrolledVisible(false);
    }
  }, [value, isControlled, onClose]);

  const eventOccurOptions = useMemo(() => {
    return optionCustomMetrics?.map((opt) => ({
      value: opt?.value,
      text: opt?.text || getLanguage(isEN, opt?.textEn, opt?.textCn),
      textEn: opt?.textEn,
      textCn: opt?.textCn,
    })) || [];
  }, [optionCustomMetrics, isEN, formatMessage]);

  /** 显示名称由 EventPanel 回传 displayNameEn/displayNameCn，按语言取；兼容旧数据 displayName */
  const displayName =
    getLanguage(
      isEN,
      tempEventFilterValue?.displayNameEn,
      tempEventFilterValue?.displayNameCn
    ) ||
    tempEventFilterValue?.displayName ||
    formatMessage({ id: 'dbl_ab_experiment_event_filter_placeholder' });

  const isPlaceholder = !tempEventFilterValue.eventName;

  const handleRename = useCallback((newDisplayName: string) => {
    onRename?.(newDisplayName);
  }, [onRename]);

  const handleDelete = useCallback(() => {
    onDelete?.();
  }, [onDelete]);

  return (
    <div className="dbl-event-filter-item">
      <EditInput
        className="dbl-event-filter-item-name"
        value={displayName}
        placeholder={formatMessage({ id: 'dbl_ab_experiment_event_filter_placeholder' })}
        onRename={handleRename}
        onDelete={handleDelete}
        editTooltip={formatMessage({ id: 'dbl_ab_experiment_event_filter_edit_name' })}
        deleteTooltip={formatMessage({ id: 'dbl_ab_experiment_event_filter_delete' })}
        displayValue={
          <Bubble content={isPlaceholder ? '' : displayName} placement="top" arrowPointAtCenter>
            <Popover
              visible={eventFilterVisible}
              onVisibleChange={handleVisibleChange}
              trigger="click"
              placement="bottom-start"
              overlayClassName="dbl-event-filter-overlay"
              overlay={
                <EventPanel
                  visible={eventFilterVisible}
                  value={tempEventFilterValue}
                  placeholderTitle={formatMessage({ id: 'dbl_ab_experiment_event_filter_placeholder_all_event' })}
                  onChange={(v) => setTempEventFilterValue(v as IEventFilterItem)}
                  onConfirm={(v) => handleEventFilterConfirm(v as IEventFilterItem)}
                  onCancel={handleEventFilterCancel}
                  hoursSinceRegisterOptions={hoursSinceRegisterOptions}
                  data={eventList}
                  displayConfig={displayConfig}
                  eventOccurOptions={eventOccurOptions}
                />
              }
            >
              <Button
                type="weak"
                className={classNames('dbl-event-filter-trigger', {
                  'dbl-event-filter-trigger--placeholder': isPlaceholder,
                })}
              >
                <span className="dbl-event-filter-trigger__text">
                  {displayName}
                </span>
                <Icon type="arrowdown" />
              </Button>
            </Popover>
          </Bubble>
        }
      />
    </div>
  );
};

export default EventFilterItem;
